// @givmo/fundraisers-module — every line the fundraiser page wizard shows (EMC-WEB6): a person creates
// their own fundraiser page under a campaign, on the web, from a Board Ask's "Create your page" or a
// campaign page's "Create your own fundraiser page". Jonathan's words, 2026-10-31 (Email Campaign plan,
// PROGRESS.md 22:05–23:04, and the approved walkthrough frames 27–30): Goal, then "Tell your story" /
// "Why KCC matters to you. It's what people read on your page.", then "Your cover photo" / "It starts
// with the appeal's photo. Change it if you'd like."; "Save and continue" and "Save and exit" on every
// step; Finish puts the page live; "Your page is live", "See your page", "Back to the ask".
//
// "KCC" and "the appeal" are the campaign's own words here: the organization's name, and what the
// campaign is (an appeal when its name says so, otherwise a campaign).

export const PAGE_WIZARD_COPY = {
  /** The top bar's label and the dialog's name. */
  title: 'Your fundraiser page',
  saveAndExit: 'Save and exit',
  close: 'Close',
  back: 'Back',
  saveAndContinue: 'Save and continue',
  finish: 'Finish',
  stepOf: (step: number, total: number) => `Step ${step} of ${total}`,
  /** Under her name: what she raises for. */
  raisingFor: (title: string) => `Raising for ${title}`,

  goalTitle: 'What’s your goal?',
  goalLine: (word: string, goal: string | null) =>
    goal
      ? `Everything your page raises counts toward the ${word}’s ${goal} goal.`
      : `Everything your page raises counts toward the ${word}.`,
  /** Under a suggested amount. */
  suggestion: 'A suggestion. Make it any amount.',
  noGoal: 'No goal',

  storyTitle: 'Tell your story',
  storyLine: (organization: string | null) =>
    organization
      ? `Why ${organization} matters to you. It’s what people read on your page.`
      : 'Why it matters to you. It’s what people read on your page.',

  coverTitle: 'Your cover photo',
  /** When the page holds the campaign's photo. */
  coverLineInherited: (word: string) =>
    `It starts with the ${word}’s photo. Change it if you’d like.`,
  /** When the page has no photo yet. */
  coverLineEmpty: 'It’s the first thing people see on your page.',
  finishNote: 'When you finish, your page goes live, with your link to share.',

  liveTitle: 'Your page is live',
  liveLine: (word: string) => `Share your link. Every gift on your page counts toward the ${word}.`,
  /** The preview's byline: "Dana Whitfield's fundraiser page". */
  pageOf: 'fundraiser page',
  copy: 'Copy',
  copied: 'Copied',
  seePage: 'See your page',

  loadFailed: 'Couldn’t open your fundraiser page. Please try again.',
  saveFailed: 'Couldn’t save your fundraiser page. Please try again.',
  uploadFailed: 'Couldn’t upload this cover photo. Please try again.',
  copyFailed: 'Couldn’t copy the link. Select it and copy it instead.',
} as const;

/** "appeal" where the campaign's name says it is one, otherwise "campaign". */
export function campaignWord(name: string): string {
  return /\bappeal\b/i.test(name) ? 'appeal' : 'campaign';
}

/**
 * The campaign as a supporter page's heading names it (walkthrough #36): its organization's possessive
 * before its name ("Kennedy Children’s Center’s 2026 Year-End Appeal"), or the name alone when it has
 * no organization or already begins with it.
 */
export function campaignTitle(name: string, organization: string | null): string {
  if (!organization || name.toLowerCase().startsWith(organization.toLowerCase())) return name;
  return `${organization}${organization.toLowerCase().endsWith('s') ? '’' : '’s'} ${name}`;
}

/** "Dana Whitfield" → "DW"; one name → its first letter. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}
